import type { MemberRetirementAges } from '#calculator/memberRetirementAges'
import { FilingStatus } from '#dictionary/filingStatuses'
import type { Region } from '#dictionary/regions'
import { getRequiredDistributionAge } from '#dictionary/requiredDistributions'
import type MemberService from '#profile/MemberService'
import type { MaritalStatus } from '#profile/schemas'

export type PersonVariables = {
  id: string
  name: string | undefined
  region: Region
  socialSecurityAge: number | null
  retirementAge: number
  currentAge: number
  requiredDistributionAge: number
  ageOnProjectionStart: number
}

type NullablePersonVariables = {
  [K in keyof PersonVariables]: PersonVariables[K] | undefined
}

export type ProfileVariables = {
  maritalStatus: MaritalStatus
  filingStatus: FilingStatus
  minorChildrenCount: number
  otherDependentsCount: number
  projectionStartYear: number
  person: PersonVariables
  partner: NullablePersonVariables
}

export type SurveyVariables = {
  profile: ProfileVariables
}

export type SurveyVariablesProfileInput = {
  maritalStatus: MaritalStatus
  filingStatus: FilingStatus
  minorChildrenCount: number
  otherDependentsCount: number
  projectionStartYear: number
  person: MemberService
  partner: MemberService | null
}

const buildPersonVariables = (
  member: MemberService,
  projectionStartYear: number,
  retirementAges: MemberRetirementAges
): PersonVariables => {
  const retirementAge = retirementAges[member.id]
  if (retirementAge === undefined) {
    throw new Error(
      `retirementAges map is missing an entry for member "${member.id}"`
    )
  }
  return {
    id: member.id,
    name: member.name,
    region: member.region,
    socialSecurityAge: member.socialSecurityAge,
    retirementAge,
    currentAge: member.currentAge,
    requiredDistributionAge: getRequiredDistributionAge(member.birthday),
    ageOnProjectionStart: member.getAgeInTheEndOfYear(projectionStartYear),
  }
}

const emptyPartner: NullablePersonVariables = {
  id: undefined,
  name: undefined,
  region: undefined,
  socialSecurityAge: undefined,
  retirementAge: undefined,
  currentAge: undefined,
  requiredDistributionAge: undefined,
  ageOnProjectionStart: undefined,
}

export const initSurveyVariables = (
  input: SurveyVariablesProfileInput,
  retirementAges: MemberRetirementAges
): SurveyVariables => {
  return {
    profile: {
      maritalStatus: input.maritalStatus,
      filingStatus: input.filingStatus,
      minorChildrenCount: input.minorChildrenCount,
      otherDependentsCount: input.otherDependentsCount,
      projectionStartYear: input.projectionStartYear,
      person: buildPersonVariables(
        input.person,
        input.projectionStartYear,
        retirementAges
      ),
      partner: input.partner
        ? buildPersonVariables(
            input.partner,
            input.projectionStartYear,
            retirementAges
          )
        : emptyPartner,
    },
  }
}
